/**
 * Codex Design System — Typography Tokens
 *
 * Brand type system — four roles:
 *   Logo/accent  — "Cinzel Decorative"  Greco-Roman display; logo & accent marks only
 *   Heading      — "Forum"              Restrained Greco-Roman serif; h1–h6, subtitles
 *   Body/UI      — "Gill Sans"          Humanist sans; navigation, labels, buttons, body
 *   Caption/meta — "Noto Sans"          Neutral, highly legible; captions, tags, metadata
 *
 * Reader fonts (user-selectable) are defined in ./readerFonts.ts.
 *
 * ── Type scale ──────────────────────────────────────────────────────────────
 * Modular scale — ratio 1.2 (minor third), base 16px / 1rem.
 *
 *   display  3.583rem   57.33px   (one step above h1, hero/landing text)
 *   h1       2.986rem   47.78px
 *   h2       2.488rem   39.81px
 *   h3       2.074rem   33.18px
 *   h4       1.728rem   27.65px
 *   h5       1.440rem   23.04px
 *   h6       1.200rem   19.20px
 *   p        1.000rem   16.00px
 *   small    0.833rem   13.33px
 *   xs       0.694rem   11.11px
 *
 * All sizes in rem — html/body base is set to 16px in GlobalStyles.
 */

import type { ThemeOptions } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Font families
// ---------------------------------------------------------------------------

export const fontFamilies = {
  /** Logo & decorative accent marks — never body text */
  logo: ['"Cinzel Decorative"', '"Palatino Linotype"', '"Book Antiqua"', 'Georgia', 'serif'].join(
    ', ',
  ),
  /** Headings h1–h6, subtitles — Forum (Greco-Roman, restrained) */
  heading: ['"Forum"', '"Palatino Linotype"', '"Book Antiqua"', 'Georgia', 'serif'].join(', '),
  /** UI body, navigation, buttons, inputs — Gill Sans (humanist sans) */
  body: ['"Gill Sans"', '"Gill Sans MT"', 'Calibri', 'sans-serif'].join(', '),
  /** Captions, metadata, labels, tags — Noto Sans (neutral, legible at small sizes) */
  caption: ['"Noto Sans"', 'system-ui', 'Arial', 'sans-serif'].join(', '),
  mono: ['Consolas', 'Monaco', '"Courier New"', 'monospace'].join(', '),
} as const;

// ---------------------------------------------------------------------------
// Font weights
// ---------------------------------------------------------------------------

export const fontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert px to rem using the 16px browser base.
 * Prefer the named `typeScale` constants for typography variants.
 * This helper remains useful in component overrides (components.ts)
 * where ad-hoc sizes are needed for spacing/icon alignment.
 */
export const pxToRem = (px: number): string => `${px / 16}rem`;

// ---------------------------------------------------------------------------
// Type scale steps
//
// Named after their semantic role rather than their pixel value so that
// callers stay readable when the scale is updated.
// ---------------------------------------------------------------------------

/** Modular scale — ratio 1.2, base 1rem = 16px */
export const typeScale = {
  display: '3.583rem', //  57.33px — hero / landing text (h1 × 1.2)
  h1: '2.986rem', //  47.78px
  h2: '2.488rem', //  39.81px
  h3: '2.074rem', //  33.18px
  h4: '1.728rem', //  27.65px
  h5: '1.440rem', //  23.04px
  h6: '1.200rem', //  19.20px
  p: '1.000rem', //  16.00px  — base
  small: '0.833rem', //  13.33px
  xs: '0.694rem', //  11.11px
} as const;

// ---------------------------------------------------------------------------
// Type scale (MUI typography config)
// ---------------------------------------------------------------------------

export const typography: ThemeOptions['typography'] = {
  fontFamily: fontFamilies.body,
  /** htmlFontSize — must match the 16px base set on <html> in GlobalStyles */
  htmlFontSize: 16,
  fontSize: 16,
  fontWeightLight: fontWeights.light,
  fontWeightRegular: fontWeights.regular,
  fontWeightMedium: fontWeights.medium,
  fontWeightBold: fontWeights.bold,

  // ── Headings ──────────────────────────────────────────────────────────────
  // Forum is a single-weight display face; weight stays at 400.
  // Line height tightens as size grows — large type needs less leading.

  h1: {
    fontFamily: fontFamilies.heading,
    fontWeight: fontWeights.regular,
    fontSize: typeScale.h1,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontFamily: fontFamilies.heading,
    fontWeight: fontWeights.regular,
    fontSize: typeScale.h2,
    lineHeight: 1.15,
    letterSpacing: '-0.015em',
  },
  h3: {
    fontFamily: fontFamilies.heading,
    fontWeight: fontWeights.regular,
    fontSize: typeScale.h3,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h4: {
    fontFamily: fontFamilies.heading,
    fontWeight: fontWeights.regular,
    fontSize: typeScale.h4,
    lineHeight: 1.25,
    letterSpacing: '-0.005em',
  },
  h5: {
    fontFamily: fontFamilies.heading,
    fontWeight: fontWeights.regular,
    fontSize: typeScale.h5,
    lineHeight: 1.3,
    letterSpacing: '0',
  },
  h6: {
    fontFamily: fontFamilies.heading,
    fontWeight: fontWeights.regular,
    fontSize: typeScale.h6,
    lineHeight: 1.35,
    letterSpacing: '0',
  },

  // ── Subtitles ─────────────────────────────────────────────────────────────
  // subtitle1: section intro text — same size as h6, slightly looser leading
  // subtitle2: UI lead-in labels — body size, distinguished by weight/family

  subtitle1: {
    fontFamily: fontFamilies.heading,
    fontWeight: fontWeights.regular,
    fontSize: typeScale.h6,
    lineHeight: 1.5,
    letterSpacing: '0',
  },
  subtitle2: {
    fontFamily: fontFamilies.body,
    fontWeight: fontWeights.medium,
    fontSize: typeScale.p,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
  },

  // ── Body ──────────────────────────────────────────────────────────────────
  // Generous line height — this is a reading-focused app.

  body1: {
    fontFamily: fontFamilies.body,
    fontWeight: fontWeights.medium,
    fontSize: typeScale.p,
    lineHeight: 1.65,
    letterSpacing: '0',
  },
  body2: {
    fontFamily: fontFamilies.body,
    fontWeight: fontWeights.medium,
    fontSize: typeScale.small,
    lineHeight: 1.55,
    letterSpacing: '0.01em',
  },

  // ── UI chrome ─────────────────────────────────────────────────────────────

  button: {
    fontFamily: fontFamilies.body,
    fontWeight: fontWeights.medium,
    fontSize: typeScale.p,
    lineHeight: 1,
    letterSpacing: '0.04em',
    textTransform: 'none' as const,
  },
  caption: {
    fontFamily: fontFamilies.caption,
    fontWeight: fontWeights.regular,
    fontSize: typeScale.small,
    lineHeight: 1.4,
    letterSpacing: '0.02em',
  },
  overline: {
    fontFamily: fontFamilies.caption,
    fontWeight: fontWeights.semibold,
    fontSize: typeScale.xs,
    lineHeight: 1,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
  },
};

/**
 * display — 3.583rem Forum, one step above h1 on the modular scale.
 * Not a default MUI variant — registered via TypeScript augmentation in index.ts.
 * Use: <Typography variant="display"> or sx={{ typography: 'display' }}
 */
export const displayVariant = {
  fontFamily: fontFamilies.heading,
  fontWeight: fontWeights.regular,
  fontSize: typeScale.display,
  lineHeight: 1.05,
  letterSpacing: '-0.025em',
} as const;
